import {
  Assignment,
  AssignmentInd,
  SmokingRooms,
  FamilyRestroom,
  SettingsInputSvideo,
  AirlineSeatFlat,
  Medication,
  Science,
  Mail,
  Bloodtype,
  SensorOccupied,
} from '@mui/icons-material';

export const HealthTimelineSettingsLabel = {
  showImagingReports: 'Imaging',
  showPathologyReports: 'Pathology',
  showBloodTest: 'Blood test',
  showPatientReported: 'Patient-reported',
  showClinicalNotes: 'Clinical note',
  showLetters: 'Letter',
};

export const TimelineTypeToSettingsKey = {
  imagingReports: 'showImagingReports',
  pathologyReports: 'showPathologyReports',
  bloodTest: 'showBloodTest',
  patientReported: 'showPatientReported',
  clinicalNotes: 'showClinicalNotes',
  letters: 'showLetters',
} as const;

export const TimelineTypeIconMap = {
  imagingReports: <SensorOccupied sx={{ color: 'white', height: '24px', width: '24px' }} />,
  pathologyReports: <Science sx={{ color: 'white', height: '24px', width: '24px' }} />,
  bloodTest: <Bloodtype sx={{ color: 'white', height: '24px', width: '24px' }} />,
  patientReported: <AssignmentInd sx={{ color: 'white', height: '24px', width: '24px' }} />,
  clinicalNotes: <Assignment sx={{ color: 'white', height: '24px', width: '24px' }} />,
  letters: <Mail sx={{ color: 'white', height: '24px', width: '24px' }} />,
};

// Mapping from backend document types to frontend filter categories
export const DocumentTypeToTimelineType = {
  RADIOLOGY_CT: 'imagingReports',
  RADIOLOGY_PET_CT: 'imagingReports',
  RADIOLOGY_MRI: 'imagingReports',
  RADIOLOGY_X_RAY: 'imagingReports',
  RADIOLOGY_ULTRASOUND: 'imagingReports',
  RADIOLOGY_BONE_SCAN: 'imagingReports',
  RADIOLOGY_BONE_DENSITY_SCAN: 'imagingReports',
  RADIOLOGY_PROCEDURE: 'imagingReports',
  RADIOLOGY_OTHER: 'imagingReports',
  PATHOLOGY_HISTOPATHOLOGY: 'pathologyReports',
  PATHOLOGY_CYTOPATHOLOGY: 'pathologyReports',
  PATHOLOGY_OTHER: 'pathologyReports',
  PATHOLOGY_BLOOD_TEST: 'bloodTest',
  DISTRESS_THERMOMETER: 'patientReported',
  HEALTH_ASSESSMENT: 'patientReported',
  LETTER: 'letters',
} as const;

// Helper function to get timeline type from document type
export const getTimelineTypeFromDocumentType = (documentType: string): keyof typeof TimelineTypeToSettingsKey => {
  return DocumentTypeToTimelineType[documentType as keyof typeof DocumentTypeToTimelineType] || 'clinicalNotes';
};

export const SummaryTypeIconMap = {
  overview: <AssignmentInd sx={{ color: 'white', height: '24px', width: '24px' }} />,
  cancerHistory: <SettingsInputSvideo sx={{ color: 'white', height: '24px', width: '24px' }} />,
  pastMedicalHistory: <AirlineSeatFlat sx={{ color: 'white', height: '24px', width: '24px' }} />,
  medicationsAndAllergies: <Medication sx={{ color: 'white', height: '24px', width: '24px' }} />,
  familyHistory: <FamilyRestroom sx={{ color: 'white', height: '24px', width: '24px' }} />,
  socialHistory: <SmokingRooms sx={{ color: 'white', height: '24px', width: '24px' }} />,
};

export const SummarySettingsLabel = {
  showOverview: '',
  showCancerHistory: 'Cancer history',
  showPastMedicalHistory: 'Past medical history & surgical history',
  showMedicationsAndAllergies: 'Medications & allergies',
  showFamilyHistory: 'Family history',
  showSocialHistory: 'Social history & functional status',
};

export const SummaryTypeTitleMap = {
  overview: 'Overview',
  cancerHistory: 'Cancer History',
  pastMedicalHistory: 'Past Medical History & Surgical History',
  medicationsAndAllergies: 'Medications & Allergies',
  familyHistory: 'Family History',
  socialHistory: 'Social History & Functional Status',
};

const BASE_QUICK_CHIPS = [
  "What is the patient's primary cancer diagnosis?",
  'When was the cancer first diagnosed?',
  'What stage and grade is the cancer?',
  'What molecular or genetic mutations have been identified?',
  'Does the patient have any comorbidities that might affect treatment?',
];

const DOCUMENT_GENERATION_CHIPS = ['Generate an initial consultation letter'];

/**
 * Get quick chips based on document generation feature flag
 * @param hasDocumentGenerationFeature - Whether document generation is enabled
 * @returns Array of quick chip labels
 */
export const getQuickChips = (hasDocumentGenerationFeature: boolean): string[] => {
  if (hasDocumentGenerationFeature) {
    return [...DOCUMENT_GENERATION_CHIPS, ...BASE_QUICK_CHIPS];
  }
  return BASE_QUICK_CHIPS;
};

/**
 * @deprecated Use getQuickChips() instead. This is kept for backwards compatibility.
 */
export const ALL_QUICK_CHIPS = [...DOCUMENT_GENERATION_CHIPS, ...BASE_QUICK_CHIPS];
